import { useState, useEffect } from 'react';
import { useComposioOAuth } from '../hooks/useComposioOAuth';
import {
	Box,
	Typography,
	Button,
	Grid,
	Card,
	CardContent,
	CardHeader,
	CardActions,
	Avatar,
	CircularProgress,
	Alert,
	Snackbar,
	Collapse,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import logoMapping from '../../assets/images/services-logos';
import { authedApiFetch } from '../../utils/Api';
import ReactMarkdown from 'react-markdown';
import ConnectionSettingsModal from './ConnectionSettingsModal';

// Helper function to render service instructions properly based on type
const renderServiceInstructions = (service) => {
	if (
		!service.settings_instructions ||
		service.settings_instructions.length === 0
	) {
		return <Typography>No additional details available.</Typography>;
	}

	return (
		<Box>
			{service.settings_instructions.map((instruction, index) => (
				<Box key={index} sx={{ mb: 2 }}>
					{instruction.label && (
						<Typography variant='subtitle2' sx={{ mt: 1 }}>
							{instruction.label}
						</Typography>
					)}

					{instruction.is_markdown ? (
						<Box sx={{ '& a': { color: 'primary.main' } }}>
							<ReactMarkdown>{instruction.content}</ReactMarkdown>
						</Box>
					) : (
						<Typography variant='body2'>{instruction.content}</Typography>
					)}
				</Box>
			))}
		</Box>
	);
};

const ConnectionNew = () => {
	const { tenantId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	// Get scope from URL query parameter
	const queryParams = new URLSearchParams(location.search);
	const scopeParam = queryParams.get('scope');

	// State variables
	const [selectedService, setSelectedService] = useState(null);
	const [showSettingsModal, setShowSettingsModal] = useState(false);

	const [expandedCards, setExpandedCards] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [notification, setNotification] = useState(null);

	// Use the custom Composio OAuth hook
	const { initiateComposioOAuth, oAuthResult } = useComposioOAuth({
		tenantId,
		navigate,
	});
	const [availableServices, setAvailableServices] = useState([]);
	const [isServicesLoading, setIsServicesLoading] = useState(false);

	// Fetch services filtered by scope
	useEffect(() => {
		const fetchFilteredServices = async () => {
			setIsServicesLoading(true);
			try {
				const params = scopeParam ? { scope: scopeParam } : null;

				const filteredServices = await authedApiFetch({
					endpoint: `/tenants/${tenantId}/services`,
					method: 'GET',
					params: params,
				});

				// Filter to only show composio services and exclude hidden/legacy ones
				const composioServices = filteredServices.filter(
					(service) =>
						service.integration_type === 'composio' && !service.hidden
				);

				setAvailableServices(composioServices);
			} catch (error) {
				console.error('Error fetching filtered services:', error);
			} finally {
				setIsServicesLoading(false);
			}
		};

		fetchFilteredServices();
	}, [scopeParam, tenantId]);

	// Handle card expansion
	const handleExpandClick = (serviceId, event) => {
		event.stopPropagation(); // Prevent triggering card click
		setExpandedCards((prev) => ({
			...prev,
			[serviceId]: !prev[serviceId],
		}));
	};

	// Handle service selection
	const handleSelectService = (service) => {
		setSelectedService(service);

		// For OAuth-based services like Composio without settings_fields, initiate OAuth flow immediately
		if (
			service.integration_type === 'composio' &&
			(!service.settings_fields || service.settings_fields.length === 0)
		) {
			setIsLoading(true);
			initiateComposioOAuth(service.id);
		}
		// For OAuth-based services with settings_fields (like SharePoint subdomain), show modal
		else if (
			service.integration_type === 'composio' &&
			service.settings_fields &&
			service.settings_fields.length > 0
		) {
			setShowSettingsModal(true);
		}
	};

	// React to OAuth result changes (success or error)
	useEffect(() => {
		if (oAuthResult) {
			if (oAuthResult.success === false) {
				setNotification({
					message: oAuthResult.error || 'Failed to connect',
					severity: 'error',
				});
			} else if (oAuthResult.success === true) {
				setNotification({
					message: 'Successfully connected!',
					severity: 'success',
				});
			}
			setIsLoading(false);

			// Return to connections list
			navigate(`/tenants/${tenantId}/connections`);
		}
	}, [oAuthResult]);

	// Handle settings modal submit
	const handleSettingsSubmit = (settings, error) => {
		if (error) {
			setNotification({ message: error, severity: 'error' });
			return;
		}

		setShowSettingsModal(false);
		setIsLoading(true);
		initiateComposioOAuth(selectedService.id, settings);
	};

	// Handle settings modal cancel
	const handleSettingsCancel = () => {
		setShowSettingsModal(false);
		setSelectedService(null);
	};

	return (
		<Box>
			<Typography variant='h4' sx={{ mb: 4 }}>
				Add New Connection
			</Typography>

			{/* Service Selection View */}
			{!selectedService && (
				<>
					<Typography variant='h5' sx={{ mb: 3 }}>
						Select a Service
					</Typography>

					{isServicesLoading ? (
						<CircularProgress />
					) : availableServices && availableServices.length > 0 ? (
						<Grid container spacing={3}>
							{availableServices.map((service) => (
								<Grid item xs={12} sm={6} md={4} key={service.id}>
									<Card
										sx={{
											height: '100%',
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										<CardHeader
											avatar={
												logoMapping[service.logo] ? (
													<Avatar
														src={logoMapping[service.logo]}
														alt={service.name}
														sx={{
															width: 28,
															height: 28,
														}}
													/>
												) : (
													<Avatar sx={{ width: 28, height: 28 }}>
														{service.name[0]}
													</Avatar>
												)
											}
											title={
												<Typography variant='subtitle1'>
													{service.name}
												</Typography>
											}
											// subheader={
											//   <Typography variant="caption" color="textSecondary">
											//     {service.category ||
											//       (service.integration_type === "composio"
											//         ? "OAuth Integration"
											//         : "System Integration")}
											//   </Typography>
											// }
											action={
												<Button
													variant='contained'
													color='primary'
													size='small'
													onClick={() => handleSelectService(service)}
												>
													{service.integration_type === 'composio'
														? 'Connect'
														: 'Configure'}
												</Button>
											}
										/>

										<CardActions disableSpacing sx={{ pt: 0, pb: 1 }}>
											<Button
												size='small'
												onClick={(e) => handleExpandClick(service.id, e)}
												startIcon={
													expandedCards[service.id] ? (
														<ExpandMoreIcon
															style={{ transform: 'rotate(180deg)' }}
														/>
													) : (
														<ExpandMoreIcon />
													)
												}
											>
												{expandedCards[service.id]
													? 'Hide details'
													: 'View details'}
											</Button>
										</CardActions>

										<Collapse
											in={expandedCards[service.id]}
											timeout='auto'
											unmountOnExit
										>
											<CardContent sx={{ pt: 0 }}>
												{renderServiceInstructions(service)}
											</CardContent>
										</Collapse>
									</Card>
								</Grid>
							))}
						</Grid>
					) : (
						<Typography>No services available</Typography>
					)}
				</>
			)}

			{/* Settings Modal for Composio services with settings_fields */}
			<ConnectionSettingsModal
				open={showSettingsModal}
				service={selectedService}
				onSubmit={handleSettingsSubmit}
				onCancel={handleSettingsCancel}
			/>

			{/* Loading states */}
			{isLoading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
					<CircularProgress />
				</Box>
			)}

			{/* TODO: move this to a parent route and expose via context/provider */}
			<Snackbar
				open={!!notification}
				autoHideDuration={6000}
				onClose={() => setNotification(null)}
			>
				<Alert
					onClose={() => setNotification(null)}
					severity={notification?.severity || 'info'}
					sx={{ width: '100%' }}
				>
					{notification?.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default ConnectionNew;
